/**
 * Página de boas-vindas da instalação vinda da Nuvemshop (landing page pós-instalação).
 *
 * A Nuvemshop pede explicitamente esta etapa na homologação: quem descobre o app
 * na Loja de Aplicativos e ainda não é cliente não pode cair direto numa tela de
 * login sem saída. Aqui ele escolhe entre entrar e criar conta.
 *
 * Se o usuário JÁ está autenticado, esta página não aparece: ela refaz a
 * autorização na Nuvemshop na hora (o `code` anterior expira em 5 minutos, então
 * pedimos um novo em vez de tentar reaproveitar).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, MessageSquare, Package, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getBackendUrl } from '../utils/api';
import {
  limparInstalacaoPendente,
  marcarInstalacaoPendente,
  resolverCompanyId,
} from '../utils/nuvemshopInstalacao';

const IAT = {
  yellow: '#FFC107',
  yellowDeep: '#F59E0B',
  graphite: '#16161c',
  charcoal: '#1e1e26',
  border: '#2f2f3a',
  silver: '#9A9AA5',
  paper: '#FAFAFB',
  ink: '#1b1b22',
};
const FONT = "'Inter', -apple-system, system-ui, sans-serif";

const Beneficio = ({ icone: Icone, titulo, texto }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left' }}>
    <Icone size={18} color={IAT.yellow} style={{ flexShrink: 0, marginTop: 2 }} />
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, color: IAT.paper }}>{titulo}</div>
      <div style={{ fontSize: 13, color: IAT.silver, lineHeight: 1.5 }}>{texto}</div>
    </div>
  </div>
);

const NuvemshopInstall = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [erro, setErro] = useState(null);
  const [retomando, setRetomando] = useState(false);
  // Consumimos a intenção ANTES de tentar: se a autorização falhar, o usuário vê
  // o erro em vez de ficar preso num vai-e-volta entre o app e a Nuvemshop.
  const jaTentou = useRef(false);

  useEffect(() => {
    if (!user) {
      marcarInstalacaoPendente();
      return;
    }
    if (jaTentou.current) return;
    jaTentou.current = true;
    limparInstalacaoPendente();
    setRetomando(true);

    (async () => {
      try {
        const companyId = await resolverCompanyId(user);
        const token = await user.getIdToken();
        const resp = await fetch(
          `${getBackendUrl()}/api/nuvemshop/oauth/start?companyId=${encodeURIComponent(companyId)}`,
          { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
        );
        const data = await resp.json();
        if (!resp.ok || !data.authorize_url) {
          throw new Error(data.error || 'Não foi possível iniciar a autorização na Nuvemshop.');
        }
        window.location.assign(data.authorize_url);
      } catch (e) {
        setRetomando(false);
        setErro(e.message || 'Não foi possível continuar a instalação.');
      }
    })();
  }, [user]);

  const caixa = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: IAT.graphite,
    color: '#e0e0ff',
    padding: 24,
    fontFamily: FONT,
  };
  const cartao = {
    maxWidth: 520,
    width: '100%',
    background: IAT.charcoal,
    border: `1px solid ${IAT.border}`,
    borderRadius: 12,
    padding: 28,
  };
  const botaoPrimario = {
    background: IAT.yellowDeep,
    color: IAT.ink,
    border: 'none',
    borderRadius: 8,
    padding: '12px 18px',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
  };
  const botaoSecundario = {
    ...botaoPrimario,
    background: 'transparent',
    color: IAT.paper,
    border: `1px solid ${IAT.border}`,
  };

  if (user && retomando) {
    return (
      <div style={caixa}>
        <div style={{ ...cartao, textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 18 }}>Autorizando sua loja…</h2>
          <p style={{ color: IAT.silver, fontSize: 14, margin: 0 }}>
            Estamos levando você à Nuvemshop para confirmar as permissões do aplicativo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={caixa}>
      <div style={cartao}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: IAT.yellow }}>
          IAT · Nuvemshop
        </div>
        <h1 style={{ margin: '10px 0 8px', fontSize: 22, color: IAT.paper, lineHeight: 1.3 }}>
          Seu atendimento no WhatsApp conectado à sua loja
        </h1>
        <p style={{ color: IAT.silver, fontSize: 14, lineHeight: 1.6, margin: '0 0 22px' }}>
          A IAT centraliza o atendimento da sua loja no WhatsApp e responde com os dados
          reais da Nuvemshop — sem que sua equipe precise procurar nada.
        </p>

        <div style={{ display: 'grid', gap: 14, marginBottom: 24 }}>
          <Beneficio
            icone={Package}
            titulo="Pedidos e rastreio na hora"
            texto="O cliente pergunta pelo pedido e recebe status de pagamento e entrega direto da sua loja."
          />
          <Beneficio
            icone={MessageSquare}
            titulo="Preço e estoque do seu catálogo"
            texto="As respostas usam o preço e a disponibilidade cadastrados — nada de valor inventado."
          />
          <Beneficio
            icone={Users}
            titulo="Time e agente de IA no mesmo painel"
            texto="Conversas, etiquetas, funil de vendas e relatórios num lugar só."
          />
        </div>

        {erro && (
          <div style={{
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)',
            color: '#fca5a5', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16,
          }}>
            {erro}
          </div>
        )}

        {/* Quem já está logado não precisa de cadastro: precisa de outra tentativa. */}
        {user ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <button
              type="button"
              style={botaoPrimario}
              onClick={() => { jaTentou.current = false; setErro(null); window.location.reload(); }}
            >
              Tentar conectar novamente <ArrowRight size={16} />
            </button>
            <button type="button" style={botaoSecundario} onClick={() => navigate('/chat?integracoes=1')}>
              Ir para o painel
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <button type="button" style={botaoPrimario} onClick={() => navigate('/register')}>
              Criar minha conta na IAT <ArrowRight size={16} />
            </button>
            <button type="button" style={botaoSecundario} onClick={() => navigate('/login')}>
              Já sou cliente — entrar
            </button>
          </div>
        )}

        <div style={{
          marginTop: 22, paddingTop: 18, borderTop: `1px solid ${IAT.border}`,
          fontSize: 13, color: IAT.silver, lineHeight: 1.7,
        }}>
          {!user && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
              <CheckCircle2 size={16} color={IAT.yellow} style={{ flexShrink: 0, marginTop: 3 }} />
              <span>
                Depois de entrar, sua loja é conectada automaticamente — você só confirma as
                permissões na tela da Nuvemshop.
              </span>
            </div>
          )}
          <div>
            Dúvidas antes de começar? Fale com a gente em{' '}
            <a href="mailto:contato@iatconsulting.com.br" style={{ color: IAT.yellow }}>
              contato@iatconsulting.com.br
            </a>{' '}
            ou veja os planos em{' '}
            <a
              href="https://www.iatconsulting.com.br/planos"
              target="_blank"
              rel="noreferrer"
              style={{ color: IAT.yellow }}
            >
              iatconsulting.com.br/planos
            </a>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default NuvemshopInstall;
